import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { serviciosDetalle } from '../data/mock';
import { ChevronRight, ArrowLeft, Cog } from 'lucide-react';

const MaquinaCard = ({ maquina }) => {
  const [activa, setActiva] = useState(0);
  const tieneGaleria = maquina.imagenes.length > 1;

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300"
      data-testid={`maquina-card-${maquina.nombre}`}
    >
      {/* Galería */}
      <div className="bg-gray-50 p-4 flex flex-col gap-3">
        <div className="relative h-64 sm:h-80 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={maquina.imagenes[activa]}
            alt={maquina.nombre}
            className="w-full h-full object-cover"
          />
        </div>
        {tieneGaleria && (
          <div className="flex gap-2">
            {maquina.imagenes.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiva(idx)}
                data-testid={`maquina-thumb-${maquina.nombre}-${idx}`}
                className={`relative h-16 w-20 rounded-md overflow-hidden border-2 transition-all ${
                  activa === idx
                    ? 'border-[#0d3b66]'
                    : 'border-transparent opacity-70 hover:opacity-100'
                }`}
              >
                <img src={img} alt={`${maquina.nombre} ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Especificaciones */}
      <div className="p-6 lg:p-8 flex flex-col justify-center">
        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
          <Cog className="text-[#0d3b66] shrink-0" size={22} />
          {maquina.nombre}
        </h3>
        <dl className="divide-y divide-gray-100 border-t border-gray-100">
          {maquina.specs.map((spec, i) => (
            <div key={i} className="flex justify-between gap-4 py-3">
              <dt className="text-sm text-gray-500">{spec.label}</dt>
              <dd className="text-sm font-semibold text-gray-900 text-right">{spec.valor}</dd>
            </div>
          ))}
        </dl>
        {maquina.specs.length === 0 && (
          <p className="text-sm text-gray-500 italic border-t border-gray-100 pt-4">
            Especificaciones técnicas disponibles próximamente.
          </p>
        )}
      </div>
    </div>
  );
};

const ServicioDetalle = () => {
  const { slug } = useParams();
  const servicio = serviciosDetalle[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  if (!servicio) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <main className="pt-40 pb-24 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Servicio no encontrado</h1>
          <Link to="/" className="text-[#0d3b66] underline" data-testid="back-home-link">
            Volver al inicio
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-28 pb-20">
        {/* Cabecera del servicio */}
        <section className="bg-[#0d3b66] text-white">
          <div className="container mx-auto px-6 lg:px-10 max-w-7xl py-12 lg:py-16">
            <nav className="flex items-center gap-1 text-sm text-blue-200 mb-5" data-testid="breadcrumb">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <ChevronRight size={16} />
              <button
                onClick={() => (window.location.href = '/#servicios')}
                className="hover:text-white transition-colors"
              >
                Servicios
              </button>
              <ChevronRight size={16} />
              <span className="text-white">{servicio.nombre}</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="servicio-titulo">
              {servicio.nombre}
            </h1>
            <p className="text-base md:text-lg text-blue-100 max-w-3xl leading-relaxed">
              {servicio.descripcion}
            </p>
          </div>
        </section>

        {/* Máquinas */}
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl py-12 lg:py-16">
          {servicio.grupos.map((grupo, gi) => (
            <div key={gi} className={gi > 0 ? 'mt-14' : ''}>
              {grupo.titulo && (
                <div className="flex items-center gap-3 mb-8">
                  <span className="h-8 w-1.5 bg-[#0d3b66] rounded-full" />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">{grupo.titulo}</h2>
                </div>
              )}
              <div className="space-y-8">
                {grupo.maquinas.map((maquina, mi) => (
                  <MaquinaCard key={mi} maquina={maquina} />
                ))}
              </div>
            </div>
          ))}

          {/* CTA volver / contacto */}
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-700 hover:text-[#0d3b66] transition-colors font-medium"
              data-testid="volver-servicios-link"
            >
              <ArrowLeft size={18} />
              Volver a servicios
            </Link>
            <button
              onClick={() => (window.location.href = '/#contacto')}
              className="btn-primary"
              data-testid="contacto-cta-btn"
            >
              Solicitar información
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ServicioDetalle;
